import { useTranslator } from '@bluebeam/react-i18n';
import BaseInput from 'components/inputs/BaseInput';
import Label from 'components/inputs/Label';
import AppStoreContext from 'contexts/AppStoreContext';
import { buttons } from 'copy/copy';
import React, { useContext, useState } from 'react';
import ModalStdHeader from '../ModalStdHeader';

interface Props {
  onSubmit: (domain: string) => void;
  style?: React.CSSProperties;
}

const EgnyteDomainModal = (props: Props) => {
  const [domain, setDomain] = useState('');
  const [shouldShowErrorMessage, setShouldShowErrorMessage] = useState(false);
  const { actions } = useContext(AppStoreContext);
  const { style } = props;
  const { translate } = useTranslator(['integrationsPage', 'buttons']);

  const handleBackClick = (): void => {
    actions.popModal();
  };

  const handleCancelClick = (): void => {
    actions.clearAllModals();
  };

  const handleContinueClick = (): void => {
    fetch(`https://${domain}.egnyte.com`, {
      mode: 'no-cors',
      method: 'GET',
    })
      .then((response) => {
        if (response.status === 404) {
          throw new Error(response.statusText);
        }
        props.onSubmit(domain);
      })
      .catch(() => {
        setShouldShowErrorMessage(true);
      });
  };

  const handleChange = (value: string): void => {
    setDomain(value);
    setShouldShowErrorMessage(true);
  };

  const isConfirmDisabled = (): boolean => {
    if (!domain) {
      return true;
    }
    const regex = /^[a-zA-Z0-9]{2,50}$/;
    return !regex.test(domain);
  };

  let additionalStyles = style;

  if (shouldShowErrorMessage) {
    additionalStyles = { ...style, borderColor: '#a71629' };
  }

  return (
    <div id="egnyte-domain-modal">
      <ModalStdHeader text={translate('integrationsPage:egnyte.modal.title')} />
      <div className="m3">
        <div className="mb4 light font-size-5 color-gray-7">
          {translate('integrationsPage:egnyte.modal.text')}
        </div>
        <div className="column">
          <div className="mb4">
            <Label
              label={translate('integrationsPage:egnyte.modal.domainLabel')}
            />
            <BaseInput
              style={additionalStyles}
              id="egnyte-domain-modal-domain"
              type="text"
              name="domain"
              autoFocus
              onChange={(evt): void => {
                handleChange(evt.target.value);
              }}
            />
            {shouldShowErrorMessage && (
              <div className="modal__error-message">
                {translate('integrationsPage:egnyte.modal.domainError')}
              </div>
            )}
          </div>
        </div>
        <div className="row px5 py3 so-vw100 w100 bgd-white-2 justify-between modal__button-row modal__button-row--with-border">
          <button
            id="egnyte-domain-modal-back-btn"
            className="btn btn-link"
            onClick={handleBackClick}
          >
            {translate('buttons:cancel')}
          </button>
          <div className="so-ml-auto">
            <button
              id="egnyte-domain-modal-cancel-btn"
              className="btn btn-link mr3"
              onClick={handleCancelClick}
            >
              {translate('buttons:cancel')}
            </button>
            <button
              id="egnyte-domain-modal-continue-btn"
              className="btn btn-primary"
              onClick={handleContinueClick}
              disabled={isConfirmDisabled()}
            >
              {translate('buttons:continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

EgnyteDomainModal.modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    top: 'calc(50vh - 350px)',
    left: 'calc(50vw - 270px)',
    bottom: 'initial',
    right: 'initial',
    width: '540px',
    height: '296px',
    padding: '55px 15px 0 15px',
  },
};

export default EgnyteDomainModal;
